import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../auth/context";
import { formatDmy, formatDmyTime } from "../format/date";

type AdminUser = {
  id: string;
  email: string;
  displayName: string | null;
  createdAt: string;
  lastSignedInAt: string | null;
  counts: {
    groups: number;
    items: number;
    timeEntries: number;
    attachments: number;
    activeSessions: number;
    pushSubscriptions: number;
  };
};

type AdminDomain = {
  id: string;
  domain: string;
  createdAt: string;
  addedByEmail: string | null;
};

type AdminInvite = {
  id: string;
  email: string;
  createdAt: string;
  lastInvitedAt: string | null;
  invitedByEmail: string | null;
  hasRegistered: boolean;
};

type Load<T> =
  | { status: "loading" }
  | { status: "ok"; data: T }
  | { status: "error"; message: string };

export function Admin() {
  const { state } = useAuth();
  const [users, setUsers] = useState<Load<AdminUser[]>>({ status: "loading" });
  const [domains, setDomains] = useState<Load<AdminDomain[]>>({
    status: "loading",
  });
  const [invites, setInvites] = useState<Load<AdminInvite[]>>({
    status: "loading",
  });

  // Hide the route for anyone who isn't the admin — surface a generic
  // "not found" so a non-admin probing the URL learns nothing.
  const accessToken =
    (state.status === "authenticated" && state.session.access_token) ||
    (state.status === "offline" && state.session.access_token) ||
    null;
  const user =
    state.status === "authenticated" || state.status === "offline"
      ? state.user
      : null;

  const authFetch = useCallback(
    async (path: string, init?: RequestInit) => {
      if (!accessToken) throw new Error("no_token");
      const res = await fetch(path, {
        ...init,
        headers: {
          ...(init?.headers ?? {}),
          Authorization: `Bearer ${accessToken}`,
          ...(init?.body ? { "Content-Type": "application/json" } : {}),
        },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${text ? ` ${text}` : ""}`);
      }
      return res.json();
    },
    [accessToken],
  );

  const reloadUsers = useCallback(async () => {
    try {
      const data = (await authFetch("/api/admin/users")) as AdminUser[];
      setUsers({ status: "ok", data });
    } catch (err) {
      setUsers({
        status: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [authFetch]);
  const reloadDomains = useCallback(async () => {
    try {
      const data = (await authFetch("/api/admin/allowed-domains")) as AdminDomain[];
      setDomains({ status: "ok", data });
    } catch (err) {
      setDomains({
        status: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [authFetch]);
  const reloadInvites = useCallback(async () => {
    try {
      const data = (await authFetch("/api/admin/invites")) as AdminInvite[];
      setInvites({ status: "ok", data });
    } catch (err) {
      setInvites({
        status: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [authFetch]);

  useEffect(() => {
    if (!accessToken || !user?.is_admin) return;
    void reloadUsers();
    void reloadDomains();
    void reloadInvites();
  }, [accessToken, user?.is_admin, reloadUsers, reloadDomains, reloadInvites]);

  if (state.status === "loading") {
    return (
      <main className="mx-auto flex min-h-full max-w-3xl items-center justify-center p-8">
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      </main>
    );
  }
  if (state.status === "unauthenticated") {
    return <Navigate to="/signin" replace />;
  }
  if (!user?.is_admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex min-h-full flex-col">
      <header className="flex flex-wrap items-baseline justify-between gap-y-2 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
            Admin
          </h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">Signed in as {user.email}</span>
        </div>
        <Link
          to="/"
          className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          ← Back to app
        </Link>
      </header>

      <main className="mx-auto w-full max-w-5xl space-y-10 p-4 sm:p-6">
        <section>
          <h2 className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Users {users.status === "ok" ? `· ${users.data.length}` : ""}
          </h2>
          {users.status === "loading" && (
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading users…</p>
          )}
          {users.status === "error" && (
            <p className="text-sm text-rose-700 dark:text-rose-400">
              Failed to load: <code className="font-mono">{users.message}</code>
            </p>
          )}
          {users.status === "ok" && users.data.length === 0 && (
            <p className="text-sm italic text-slate-500 dark:text-slate-400">No users yet.</p>
          )}
          {users.status === "ok" && users.data.length > 0 && (
            <UserTable users={users.data} />
          )}
        </section>

        <InvitesSection
          state={invites}
          authFetch={authFetch}
          onChanged={() => {
            void reloadInvites();
            void reloadUsers();
          }}
        />

        <DomainsSection
          state={domains}
          authFetch={authFetch}
          onChanged={reloadDomains}
        />
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invitations
// ---------------------------------------------------------------------------

function InvitesSection({
  state,
  authFetch,
  onChanged,
}: {
  state: Load<AdminInvite[]>;
  authFetch: (path: string, init?: RequestInit) => Promise<unknown>;
  onChanged: () => void;
}) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{
    kind: "ok" | "err";
    message: string;
  } | null>(null);

  const send = async (addr: string, sendEmail: boolean) => {
    setBusy(true);
    setFlash(null);
    try {
      await authFetch("/api/admin/invites", {
        method: "POST",
        body: JSON.stringify({ email: addr, send_email: sendEmail }),
      });
      setEmail("");
      setFlash({
        kind: "ok",
        message: sendEmail
          ? `Invite sent to ${addr}.`
          : `Added ${addr} to the allowlist (no email sent).`,
      });
      onChanged();
    } catch (err) {
      setFlash({
        kind: "err",
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string, addr: string) => {
    if (!confirm(`Remove ${addr} from the invite list?`)) return;
    try {
      await authFetch(`/api/admin/invites/${id}`, { method: "DELETE" });
      onChanged();
    } catch (err) {
      setFlash({
        kind: "err",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  return (
    <section>
      <h2 className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Invitations
        {state.status === "ok" ? ` · ${state.data.length}` : ""}
      </h2>

      <form
        className="mb-3 flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = email.trim().toLowerCase();
          if (!trimmed) return;
          void send(trimmed, true);
        }}
      >
        <input
          type="email"
          required
          placeholder="person@example.com"
          className="min-w-0 flex-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
        />
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Send magic link
        </button>
        <button
          type="button"
          disabled={busy || !email.trim()}
          onClick={() => void send(email.trim().toLowerCase(), false)}
          className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          title="Add to the allowlist without sending an email"
        >
          Allow without emailing
        </button>
      </form>

      {flash && (
        <p
          className={`mb-3 text-xs ${
            flash.kind === "ok" ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-400"
          }`}
        >
          {flash.message}
        </p>
      )}

      {state.status === "loading" && (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading invites…</p>
      )}
      {state.status === "error" && (
        <p className="text-sm text-rose-700 dark:text-rose-400">
          Failed to load: <code className="font-mono">{state.message}</code>
        </p>
      )}
      {state.status === "ok" && state.data.length === 0 && (
        <p className="text-sm italic text-slate-500 dark:text-slate-400">No invitations yet.</p>
      )}
      {state.status === "ok" && state.data.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-xs">
            <thead className="bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-300">
              <tr>
                <Th>Email</Th>
                <Th>Invited</Th>
                <Th>Last sent</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {state.data.map((i) => (
                <tr key={i.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900 dark:text-slate-100">{i.email}</div>
                    {i.invitedByEmail && (
                      <div className="text-[11px] text-slate-500 dark:text-slate-400">
                        by {i.invitedByEmail}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-300">
                    {formatDmy(i.createdAt)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-300">
                    {i.lastInvitedAt ? formatDmyTime(i.lastInvitedAt) : "—"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {i.hasRegistered ? (
                      <span className="rounded bg-emerald-100 dark:bg-emerald-900 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-800 dark:text-emerald-200">
                        Registered
                      </span>
                    ) : (
                      <span className="rounded bg-amber-100 dark:bg-amber-900 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:text-amber-200">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <button
                      type="button"
                      onClick={() => void send(i.email, true)}
                      className="mr-2 text-sky-700 dark:text-sky-400 underline-offset-2 hover:underline"
                    >
                      Re-send
                    </button>
                    <button
                      type="button"
                      onClick={() => void revoke(i.id, i.email)}
                      className="text-rose-700 dark:text-rose-400 underline-offset-2 hover:underline"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Approved domains
// ---------------------------------------------------------------------------

function DomainsSection({
  state,
  authFetch,
  onChanged,
}: {
  state: Load<AdminDomain[]>;
  authFetch: (path: string, init?: RequestInit) => Promise<unknown>;
  onChanged: () => void;
}) {
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{
    kind: "ok" | "err";
    message: string;
  } | null>(null);

  const add = async () => {
    const trimmed = domain.trim().replace(/^@/, "").toLowerCase();
    if (!trimmed) return;
    setBusy(true);
    setFlash(null);
    try {
      await authFetch("/api/admin/allowed-domains", {
        method: "POST",
        body: JSON.stringify({ domain: trimmed }),
      });
      setDomain("");
      setFlash({ kind: "ok", message: `Added @${trimmed}.` });
      onChanged();
    } catch (err) {
      setFlash({
        kind: "err",
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string, dom: string) => {
    if (
      !confirm(
        `Remove @${dom}? Existing users with this domain keep their accounts, but new sign-ins from this domain will be blocked.`,
      )
    ) {
      return;
    }
    try {
      await authFetch(`/api/admin/allowed-domains/${id}`, { method: "DELETE" });
      onChanged();
    } catch (err) {
      setFlash({
        kind: "err",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  return (
    <section>
      <h2 className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Approved domains
        {state.status === "ok" ? ` · ${state.data.length}` : ""}
      </h2>
      <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
        Anyone whose email is at one of these domains can sign in without an
        individual invite.
      </p>

      <form
        className="mb-3 flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <input
          type="text"
          required
          placeholder="example.com"
          className="min-w-0 flex-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          disabled={busy}
        />
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Approve domain
        </button>
      </form>

      {flash && (
        <p
          className={`mb-3 text-xs ${
            flash.kind === "ok" ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-400"
          }`}
        >
          {flash.message}
        </p>
      )}

      {state.status === "loading" && (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading domains…</p>
      )}
      {state.status === "error" && (
        <p className="text-sm text-rose-700 dark:text-rose-400">
          Failed to load: <code className="font-mono">{state.message}</code>
        </p>
      )}
      {state.status === "ok" && state.data.length === 0 && (
        <p className="text-sm italic text-slate-500 dark:text-slate-400">No approved domains.</p>
      )}
      {state.status === "ok" && state.data.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-xs">
            <thead className="bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-300">
              <tr>
                <Th>Domain</Th>
                <Th>Added</Th>
                <Th>Added by</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {state.data.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                  <td className="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">
                    @{d.domain}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-300">
                    {formatDmy(d.createdAt)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-300">
                    {d.addedByEmail ?? "—"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <button
                      type="button"
                      onClick={() => void remove(d.id, d.domain)}
                      className="text-rose-700 dark:text-rose-400 underline-offset-2 hover:underline"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function UserTable({ users }: { users: AdminUser[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-xs">
        <thead className="bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-300">
          <tr>
            <Th>Email</Th>
            <Th>Joined</Th>
            <Th>Last sign-in</Th>
            <Th align="right">Groups</Th>
            <Th align="right">Items</Th>
            <Th align="right">Time</Th>
            <Th align="right">Attach</Th>
            <Th align="right">Sessions</Th>
            <Th align="right">Push</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {users.map((u) => (
            <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
              <td className="px-3 py-2">
                <div className="font-medium text-slate-900 dark:text-slate-100">{u.email}</div>
                {u.displayName && (
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">
                    {u.displayName}
                  </div>
                )}
                <div
                  className="font-mono text-[10px] text-slate-400 dark:text-slate-500"
                  title={u.id}
                >
                  {u.id.slice(0, 8)}…
                </div>
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-300">
                {formatDmy(u.createdAt)}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-300">
                {u.lastSignedInAt ? formatDmyTime(u.lastSignedInAt) : "—"}
              </td>
              <Td align="right">{u.counts.groups}</Td>
              <Td align="right">{u.counts.items}</Td>
              <Td align="right">{u.counts.timeEntries}</Td>
              <Td align="right">{u.counts.attachments}</Td>
              <Td align="right">{u.counts.activeSessions}</Td>
              <Td align="right">{u.counts.pushSubscriptions}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={`px-3 py-2 text-[10px] font-medium uppercase tracking-wide ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}
function Td({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <td
      className={`px-3 py-2 tabular-nums text-slate-700 dark:text-slate-200 ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </td>
  );
}
